/**
 * Moderation tab — user-reported posts + auto-flagged posts, both with
 * Approve/Hide/Delete actions.
 */
import { Shield, Eye, EyeOff, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";

export function ModerationTab({ reports, flagged, busy, onModerate }) {
    return (
        <>
            <ReportsList reports={reports} busy={busy} onModerate={onModerate} />
            <AutoFlaggedList flagged={flagged} busy={busy} onModerate={onModerate} />
        </>
    );
}

function ReportsList({ reports, busy, onModerate }) {
    return (
        <div className="border border-border bg-card rounded p-4 mb-4">
            <div className="flex items-center gap-2 mb-3">
                <Shield className="w-4 h-4 text-primary" />
                <div className="overline">User-reported posts ({reports.length})</div>
            </div>
            <p className="text-xs text-muted-foreground mb-4">
                Posts flagged by community members. Review the content + reporter reasons,
                then approve (mark safe), hide (soft-remove), or delete (hard-remove).
            </p>
            <div className="space-y-3">
                {reports.length === 0 && (
                    <div className="text-sm text-muted-foreground text-center py-6">No user reports right now.</div>
                )}
                {reports.map((p) => (
                    <ReportCard key={p.id} post={p} busy={busy} onModerate={onModerate} />
                ))}
            </div>
        </div>
    );
}

function ReportCard({ post: p, busy, onModerate }) {
    return (
        <div className="border border-danger/40 bg-danger/5 rounded p-3" data-testid={`mod-report-${p.id}`}>
            <div className="flex items-center justify-between mb-2">
                <div className="font-semibold text-sm">
                    {p.author} <span className="text-xs text-muted-foreground mono">· {new Date(p.ts).toLocaleString("en-KE")}</span>
                </div>
                <span className="text-xs uppercase font-bold text-danger">
                    {p.report_count} report{p.report_count !== 1 ? "s" : ""}
                </span>
            </div>
            <p className="text-sm mb-3 leading-relaxed">{p.content}</p>
            {p.reports?.length > 0 && (
                <div className="text-xs text-muted-foreground mb-3 space-y-1">
                    {p.reports.slice(0, 3).map((r, i) => (
                        <div key={`${r.reporter_name || "anon"}-${i}`} className="italic">
                            → "{r.reason}" <span className="mono">· {r.reporter_name}</span>
                        </div>
                    ))}
                </div>
            )}
            <ModerationButtons postId={p.id} busy={busy} onModerate={onModerate} withTestIds />
        </div>
    );
}

function AutoFlaggedList({ flagged, busy, onModerate }) {
    return (
        <div className="border border-border bg-card rounded p-4">
            <div className="flex items-center gap-2 mb-3">
                <Shield className="w-4 h-4 text-accent" />
                <div className="overline">Auto-flagged posts ({flagged.length})</div>
            </div>
            <p className="text-xs text-muted-foreground mb-4">
                Posts caught by the regex filter (scam, guaranteed, ponzi, pump/dump, 100% return).
                Review manually — regex has false positives.
            </p>
            <div className="space-y-3">
                {flagged.length === 0 && (
                    <div className="text-sm text-muted-foreground text-center py-6">No auto-flagged posts.</div>
                )}
                {flagged.map((p) => (
                    <div key={p.id} className="border border-accent/40 bg-accent/5 rounded p-3" data-testid={`mod-flagged-${p.id}`}>
                        <div className="flex items-center justify-between mb-2">
                            <div className="font-semibold text-sm">
                                {p.author} <span className="text-xs text-muted-foreground mono">· {new Date(p.ts).toLocaleString("en-KE")}</span>
                            </div>
                            <span className="text-xs uppercase font-bold text-accent">AUTO-FLAG</span>
                        </div>
                        <p className="text-sm mb-3 leading-relaxed">{p.content}</p>
                        <ModerationButtons postId={p.id} busy={busy} onModerate={onModerate} />
                    </div>
                ))}
            </div>
        </div>
    );
}

function ModerationButtons({ postId, busy, onModerate, withTestIds = false }) {
    const tid = (a) => (withTestIds ? { "data-testid": `mod-${a}-${postId}` } : {});
    return (
        <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => onModerate(postId, "approve")} disabled={busy} {...tid("approve")}>
                <Eye className="w-3.5 h-3.5 mr-1" /> Approve
            </Button>
            <Button size="sm" variant="outline" onClick={() => onModerate(postId, "hide")} disabled={busy} {...tid("hide")}>
                <EyeOff className="w-3.5 h-3.5 mr-1" /> Hide
            </Button>
            <Button
                size="sm"
                variant="outline"
                onClick={() => onModerate(postId, "delete")}
                disabled={busy}
                className="text-danger border-danger/40 hover:bg-danger/10"
                {...tid("delete")}
            >
                <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
            </Button>
        </div>
    );
}
